import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    Select,
    TextField,
    Tooltip,
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import DeleteIcon from '@material-ui/icons/Delete';
import Alert from '@material-ui/lab/Alert';

import notification from 'app/config/notification';

import DialogComponent from 'app/components/DialogComponent';
import FileField from 'app/components/FileField';
import certificationEndpoints from 'app/api/services/certificationEndpoints';
import companyEndpoints from 'app/api/services/companyEndpoints';

import { errorHandler } from 'app/utils/errorHandler';
import { SERVICES_CODES } from 'app/config/services';
import { useDispatch } from 'react-redux';

function Modal(props) {
    const [changingDate, setChangingDate] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingCompany, setLoadingCompany] = useState(false);
    const [sending, setSending] = useState(false);
    const [vehiclesAvailable, setVehiclesAvailable] = useState([]);
    const [vehicle, setVehicle] = useState(null);
    const [initialExpirationDate, setInitialExpirationDate] = useState(null);
    const [company, setCompany] = useState({});
    const [dtEmissaoCrc, setDtEmissaoCrc] = useState(new Date());
    const [vehicleOpened, setVehicleOpened] = useState(null);

    const {
        classes,
        open,
        onClose,
        certification,
        request,
        requestJsonData,
        onSave,
        downloadFile,
        updateFile,
        setUpdateFile,
        vehicles,
        usedVehicles,
        isVehicleReplace,
    } = props;

    const certNumber = useMemo(() => {
        if (certification?.licenca) {
            return certification.licenca.nuLicencaFmt;
        }
        if (certification?.cdCertificado) {
            return String(certification.cdCertificado).padStart(3, '0');
        }
        return '';
    }, [certification]);

    const dispatch = useDispatch();

    useEffect(() => {
        const init = () => {
            if (open) {
                if (certification?.cdVeiculo) {
                    let found;
                    found = vehicles.filter((v) => v.cdVeiculo === certification.cdVeiculo);
                    if (found) {
                        setVehicleOpened(found[0]);
                        return found[0];
                    }
                }
            }
        };
        init();
    }, [open, certification, isVehicleReplace, vehicles]);

    const [certificationInitialValue, setCertificationInitialValue] = useState(() => {
        if (certification) {
            setDtEmissaoCrc(certification.dtEmissaoCrc || new Date());
            return certification;
        }
        return {
            cdVeiculo: null,
            deEmpresa: '',
            dtEmissaoCrc: new Date(),
            dtVencimentoCrc: null,
            deObservacao: '',
        };
    });

    const disableDate = useMemo(() => {
        if (request.cdServico === SERVICES_CODES.LICENCA_SEGUNDA_VIA) {
            return true;
        }
        return certificationInitialValue.idFileCrc && !updateFile;
    }, [certificationInitialValue, request, updateFile]);

    useEffect(() => {
        const init = async () => {
            if (open) {
                if (certification) {
                    setCertificationInitialValue(certification);
                }
                if (vehicles) {
                    let temp = _.cloneDeep(vehicles);
                    let key = 'cdVeiculo';
                    if (isVehicleReplace) {
                        key = 'cdVeiculoAntigo';
                    }
                    temp = temp
                        .filter((vv) => vv[key] && !usedVehicles.includes(vv.cdVeiculo))
                        .reduce((acumulador, elementoAtual) => {
                            //TIRAR REPETIDO
                            const found = _.find(acumulador, ['cdVeiculo', elementoAtual.cdVeiculo]);
                            if (!found) {
                                acumulador.push(elementoAtual);
                            }
                            return acumulador;
                        }, []);

                    setVehiclesAvailable(temp);
                }
                if (request.cdEmpresa) {
                    setLoadingCompany(true);
                    const response = await companyEndpoints.getCompany({ cdEmpresa: request.cdEmpresa });
                    if (response?.status === 200 && response?.data) {
                        setCompany(response.data);
                    }
                    setLoadingCompany(false);
                }
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isVehicleReplace]);

    const onChangeDate = async (values, field, date, vehi) => {
        setChangingDate(changingDate + 1);
        values[field] = date;

        if (field === 'dtEmissaoCrc') {
            await changingExpirationDate(date, values, vehi);
        }
    };

    const changingExpirationDate = async (value, values, vehi) => {
        if (moment.utc(value).isValid()) {
            setLoading(true);
            const date = await searchExpirationDate(value, vehi);
            onChangeDate(values, 'dtVencimentoCrc', date);
            setInitialExpirationDate(date);
            setLoading(false);
        }
    };

    const searchExpirationDate = async (createDate, vehi) => {
        try {
            if (vehicle || vehi) {
                const search = {
                    cdSolicitacao: request.cdSolicitacao,
                    dtEmissaoCrc: moment.utc(createDate).format('YYYY-MM-DD'),
                    cdVeiculo: vehi ? vehi : vehicle.cdVeiculo ? vehicle.cdVeiculo : vehicle,
                };
                if (requestJsonData.cdTipoEmpresa) {
                    search.cdTipoEmpresa = Number(requestJsonData.cdTipoEmpresa);
                }
                if (certNumber) {
                    search.numeroCrc = certNumber;
                }
                const response = await certificationEndpoints.getExpirationDate(search);
                if (response.status === 200 && response.data) {
                    return moment.utc(response.data);
                }
            } else {
                notification('Selecione o veículo', {
                    toastId: 'SELECT_VEHICLE',
                    type: 'warning',
                });
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        return null;
    };

    const onCancel = () => {
        setUpdateFile(false);
        onClose();
    };

    const deleteFile = (values) => {
        setUpdateFile(true);
        values.updateFile = true;
    };

    const onChangeVehicle = async (e, values) => {
        const date = values.dtEmissaoCrc || new Date();
        const value = Number(e.target.value);
        values['cdVeiculo'] = value ? value : null;
        setVehicle(value);
        await onChangeDate(values, 'dtEmissaoCrc', date, value);
    };

    const handleSave = async (values) => {
        if (values.idFileCrc && !updateFile) {
            notification('Para alterar dados do certificado, é necessário remover o certificado atual.', {
                toastId: 'DELETE_FILE_NEEDED',
                type: 'warning',
            });
            return;
        }

        setSending(true);
        if (!values.dtVencimentoCrc || values.dtVencimentoCrc === '') {
            values.dtVencimentoCrc = await searchExpirationDate(values);
        }

        if (vehicleOpened) {
            values.cdVeiculo = Number(vehicleOpened.cdVeiculo);
        } else {
            values.cdVeiculo = Number(vehicle);
        }

        if (!values.cdVeiculo) {
            notification('Selecione o veículo', {
                toastId: 'SELECT_VEHICLE',
                type: 'warning',
            });
            setSending(false);
            return;
        }

        await onSave(values);
        setSending(false);
        setVehicle(null);
    };

    const validationSchema = Yup.object().shape({
        cdVeiculo: Yup.string().required('Campo obrigatório').nullable(),
        dtEmissaoCrc: Yup.date().required('Campo obrigatório').typeError('Data inválida').nullable(),
        dtVencimentoCrc: Yup.date().required('Campo obrigatório').typeError('Data inválida').nullable(),
    });

    return (
        <DialogComponent title={'Detalhamento do certificado'} open={open} onClose={onClose}>
            <Formik
                initialValues={certificationInitialValue}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    handleSave(values);
                }}>
                {({ values, errors, validateForm }) => {
                    return (
                        <Form className={classes.containerForm}>
                            <Grid container spacing={1} className={classes.fieldsContainer}>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Número</InputLabel>
                                    <Field
                                        name=""
                                        disabled
                                        variant="filled"
                                        margin="dense"
                                        value={certNumber}
                                        fullWidth
                                        as={TextField}
                                    />
                                </Grid>
                                {vehicleOpened && (
                                    <Grid item xs={12} sm={4}>
                                        <InputLabel>Placa</InputLabel>
                                        <Field
                                            name=""
                                            disabled
                                            variant="filled"
                                            margin="dense"
                                            value={vehicleOpened.veiculo?.dePlaca}
                                            fullWidth
                                            as={TextField}
                                        />
                                    </Grid>
                                )}
                                {!vehicleOpened && (
                                    <Grid item xs={12} sm={4}>
                                        <InputLabel>Placa</InputLabel>
                                        <FormControl variant="filled" margin="dense" fullWidth>
                                            <Field
                                                name="cdVeiculo"
                                                id="cdVeiculo"
                                                native
                                                as={Select}
                                                value={vehicle}
                                                onChange={(e) => onChangeVehicle(e, values)}
                                                error={!!errors.cdVeiculo}>
                                                <option aria-label="None" value="">
                                                    Selecione
                                                </option>
                                                {vehiclesAvailable.map((v) => {
                                                    let cdVeiculo = v.cdVeiculo;
                                                    let dePlaca = v.veiculo?.dePlaca;
                                                    return (
                                                        <option key={`ve-${v.cdVeiculo}`} value={cdVeiculo}>
                                                            {dePlaca}
                                                        </option>
                                                    );
                                                })}
                                            </Field>
                                            {errors.cdVeiculo ? (
                                                <FormHelperText>{errors.cdVeiculo}</FormHelperText>
                                            ) : null}
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container spacing={1} className={classes.fieldsContainer}>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Empresa</InputLabel>
                                    {loadingCompany && (
                                        <div className={classes.progress}>
                                            <CircularProgress size={20} />
                                        </div>
                                    )}
                                    {!loadingCompany && (
                                        <Field
                                            name="deEmpresa"
                                            disabled
                                            variant="filled"
                                            margin="dense"
                                            fullWidth
                                            value={
                                                company.cdTipoPessoaFisica ? company.nmCompleto : company.deRazaoSocial
                                            }
                                            as={TextField}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Emissão</InputLabel>
                                    <Field
                                        name="dtEmissaoCrc"
                                        disabled={disableDate}
                                        disableToolbar
                                        format="DD/MM/YYYY"
                                        emptyLabel={''}
                                        autoOk
                                        clearable
                                        clearLabel={'Limpar'}
                                        value={
                                            dtEmissaoCrc ? moment.utc(dtEmissaoCrc).format('YYYY-MM-DD') : new Date()
                                        }
                                        invalidLabel="Data inválida"
                                        invalidDateMessage="Data inválida"
                                        maxDateMessage="Data inválida"
                                        minDateMessage="Data inválida"
                                        margin="dense"
                                        onChange={(v) => onChangeDate(values, 'dtEmissaoCrc', v)}
                                        KeyboardButtonProps={{
                                            'aria-label': 'change date',
                                        }}
                                        inputVariant="filled"
                                        fullWidth
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        as={KeyboardDatePicker}
                                        error={!!errors.dtEmissaoCrc}
                                        helperText={errors.dtEmissaoCrc ? errors.dtEmissaoCrc : ''}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Vencimento</InputLabel>
                                    {loading && (
                                        <div className={classes.progress}>
                                            <CircularProgress size={20} />
                                        </div>
                                    )}
                                    {!loading && (
                                        <Field
                                            name="dtVencimentoCrc"
                                            disabled={disableDate}
                                            disableToolbar
                                            format="DD/MM/YYYY"
                                            emptyLabel={''}
                                            autoOk
                                            clearable
                                            clearLabel={'Limpar'}
                                            value={
                                                values.dtVencimentoCrc
                                                    ? moment.utc(values.dtVencimentoCrc).format('YYYY-MM-DD')
                                                    : initialExpirationDate
                                            }
                                            invalidLabel="Data inválida"
                                            invalidDateMessage="Data inválida"
                                            maxDateMessage="Data inválida"
                                            minDateMessage="Data inválida"
                                            margin="dense"
                                            onChange={(v) => onChangeDate(values, 'dtVencimentoCrc', v)}
                                            KeyboardButtonProps={{
                                                'aria-label': 'change date',
                                            }}
                                            inputVariant="filled"
                                            fullWidth
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            as={KeyboardDatePicker}
                                            error={!!errors.dtVencimentoCrc}
                                            helperText={errors.dtVencimentoCrc ? errors.dtVencimentoCrc : ''}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <InputLabel>Observações</InputLabel>
                                    <Field
                                        name="deObservacao"
                                        disabled={values.idFileCrc && !updateFile}
                                        variant="filled"
                                        margin="dense"
                                        multiline
                                        value={values.deObservacao ? values.deObservacao : ''}
                                        minRows={4}
                                        fullWidth
                                        as={TextField}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                {values.idFileCrc && !updateFile && (
                                    <>
                                        <Grid item xs={12} className={classes.containerIcons}>
                                            <Tooltip title="Excluir arquivo atual e gerar novo">
                                                <IconButton
                                                    className={classes.btnDeleteFile}
                                                    size="small"
                                                    onClick={() => deleteFile(values)}>
                                                    <DeleteIcon className={classes.iconDelete} />
                                                </IconButton>
                                            </Tooltip>
                                            <Button
                                                color="primary"
                                                className={classes.btnActualFile}
                                                component="span"
                                                disableElevation
                                                onClick={async () =>
                                                    await downloadFile({ idFile: values.idFileCrc, dispatch })
                                                }>
                                                <InsertDriveFileIcon className={classes.icon} fontSize="inherit" /> Ver
                                                arquivo atual
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Alert severity="warning">
                                                Atenção: Para alterar dados do certificado, é necessário remover o
                                                certificado atual.
                                            </Alert>
                                        </Grid>
                                    </>
                                )}
                                {(!values.idFileCrc || updateFile) && (
                                    <Grid item xs={12} sm={6} md={6}>
                                        <Field
                                            value={{}}
                                            required={true}
                                            name={`doc-1`}
                                            label={'Carregar arquivo'}
                                            id={`doc-1`}
                                            component={FileField}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container className={classes.actionsContainer}>
                                {sending && (
                                    <Grid item xs={12} className={classes.centerProgress}>
                                        <CircularProgress size={20} />
                                    </Grid>
                                )}
                                {!sending && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <Button className={classes.actionsBtns} onClick={() => onCancel()}>
                                                <FormattedMessage id="LABELS.COMMONS.CANCEL" />
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm={4} className={classes.textCenter}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className={classes.actionsBtns}
                                                onClick={() => {
                                                    validateForm().then(async (res) => {
                                                        if (Object.keys(res).length <= 0) {
                                                            handleSave(values);
                                                        } else {
                                                            notification(
                                                                'O(s) campo(s) sinalizado(s) precisam de revisão.',
                                                                {
                                                                    toastId: 'VALIDATION',
                                                                    type: 'error',
                                                                },
                                                            );
                                                        }
                                                    });
                                                }}>
                                                <Box className={classes.colorWhite}>Salvar / Gerar </Box>
                                            </Button>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Form>
                    );
                }}
            </Formik>
        </DialogComponent>
    );
}

export default injectIntl(Modal);
